// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — the two-card board: a country silhouette + name per card, a "which has
// more X?" prompt, and the count-up reveal. DOM/CSS (crisp text, big tap targets).
// The pure state lives in game.ts; this only draws it and animates the reveal. The
// count-up uses setInterval, not rAF, so it still runs in a backgrounded tab.

import { country, type Country } from './data';
import { silhouette } from './geo';
import { escapeHtml } from './dom';
import { fmtStat } from './format';
import type { Pair } from './seed';
import type { Side } from './game';

export interface BoardVM {
  pair: Pair;
  statLabel: string;
  phase: 'ask' | 'reveal';
  pickedSide?: Side | null;
  correctSide?: Side;
  reducedMotion: boolean;
}

// ── reveal pacing ────────────────────────────────────────────────────────────
// On reveal the values count up (COUNT_UP_MS) and then must sit at their true
// value long enough to actually be READ before the board advances — the whole
// point of the reveal is to show the final numbers. REVEAL_MS is the total
// on-screen time, so the readable dwell is REVEAL_MS − COUNT_UP_MS.
export const COUNT_UP_TICKS = 16;
export const COUNT_UP_TICK_MS = 40;
/** How long the count-up animation takes to settle on the true value. */
export const COUNT_UP_MS = COUNT_UP_TICKS * COUNT_UP_TICK_MS;
/** Total time the reveal stays up before the next pair (or results). Chosen to
 *  leave a readable beat once the count-up settles, so the final numbers can
 *  actually be seen (outsize#2). */
export const REVEAL_MS = 1500;

export interface Board {
  render(vm: BoardVM): void;
  destroy(): void;
}

function silhouetteSvg(c: Country): string {
  const s = silhouette(c);
  if (!s.d) return '';
  return `<svg viewBox="0 0 ${s.w} ${s.h}" preserveAspectRatio="xMidYMid meet" aria-hidden="true"><path d="${s.d}"/></svg>`;
}

function cardHtml(side: Side): string {
  return `
    <button class="card" data-side="${side}" type="button" aria-label="Pick this country">
      <span class="card-mark" aria-hidden="true"></span>
      <span class="card-flag"></span>
      <span class="card-name"></span>
      <span class="card-cont"></span>
      <span class="card-val" aria-live="off"></span>
    </button>`;
}

export function createBoard(host: HTMLElement, onPick: (side: Side) => void): Board {
  host.innerHTML = `
    <div class="board">
      ${cardHtml('left')}
      <div class="board-mid"><span class="prompt" id="prompt"></span><span class="vs">vs</span></div>
      ${cardHtml('right')}
    </div>`;

  const cards: Record<Side, HTMLButtonElement> = {
    left: host.querySelector<HTMLButtonElement>('.card[data-side="left"]')!,
    right: host.querySelector<HTMLButtonElement>('.card[data-side="right"]')!,
  };
  const promptEl = host.querySelector<HTMLElement>('#prompt')!;

  const onClick = (side: Side) => (): void => onPick(side);
  const handlers: Record<Side, () => void> = { left: onClick('left'), right: onClick('right') };
  cards.left.addEventListener('click', handlers.left);
  cards.right.addEventListener('click', handlers.right);

  let shownPairKey = '';
  // One interval PER card — a single shared handle orphans the first card's timer,
  // which then writes a stale value onto the next pair after the ask render.
  let countTimers: ReturnType<typeof setInterval>[] = [];
  const stopCount = (): void => {
    for (const t of countTimers) clearInterval(t);
    countTimers = [];
  };

  function paintFace(side: Side, c: Country): void {
    const el = cards[side];
    el.querySelector('.card-flag')!.innerHTML = silhouetteSvg(c);
    el.querySelector('.card-name')!.textContent = c.name;
    el.querySelector('.card-cont')!.textContent = c.cont;
  }

  function countUp(el: HTMLElement, target: number, stat: Pair['stat'], instant: boolean): void {
    if (instant) {
      el.textContent = fmtStat(target, stat);
      return;
    }
    // setInterval (not rAF), so it still advances in a backgrounded tab; a final write
    // guarantees the true value even if the interval is throttled to a single tick.
    const total = COUNT_UP_TICKS;
    let ticks = 0;
    const id = setInterval(() => {
      ticks++;
      const t = Math.min(1, ticks / total);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = fmtStat(target * eased, stat);
      if (ticks >= total) {
        clearInterval(id);
        countTimers = countTimers.filter((x) => x !== id);
        el.textContent = fmtStat(target, stat);
      }
    }, COUNT_UP_TICK_MS);
    countTimers.push(id);
  }

  function render(vm: BoardVM): void {
    const { pair, phase } = vm;
    const left = country(pair.leftId)!;
    const right = country(pair.rightId)!;
    const key = `${pair.leftId}|${pair.rightId}|${pair.stat}`;
    if (key !== shownPairKey) {
      shownPairKey = key;
      paintFace('left', left);
      paintFace('right', right);
      if (!vm.reducedMotion) {
        cards.left.classList.remove('slide-in');
        cards.right.classList.remove('slide-in');
        void cards.left.offsetWidth;
        cards.left.classList.add('slide-in');
        cards.right.classList.add('slide-in');
      }
    }
    promptEl.innerHTML = `Which has the greater <b>${escapeHtml(vm.statLabel)}</b>?`;

    stopCount();
    for (const side of ['left', 'right'] as Side[]) {
      const el = cards[side];
      el.classList.remove('bigger', 'wrong', 'revealed', 'dim');
      const valEl = el.querySelector<HTMLElement>('.card-val')!;
      const markEl = el.querySelector<HTMLElement>('.card-mark')!;
      if (phase === 'ask') {
        el.disabled = false;
        valEl.textContent = '';
        markEl.textContent = '';
      } else {
        el.disabled = true;
        el.classList.add('revealed');
        const isBigger = vm.correctSide === side;
        const val = side === 'left' ? pair.leftVal : pair.rightVal;
        countUp(valEl, val, pair.stat, vm.reducedMotion);
        if (isBigger) {
          el.classList.add('bigger');
          markEl.textContent = '✓';
        } else {
          el.classList.add('dim');
          if (vm.pickedSide === side) {
            el.classList.add('wrong');
            markEl.textContent = '✗';
          }
        }
      }
    }
  }

  return {
    render,
    destroy() {
      stopCount();
      cards.left.removeEventListener('click', handlers.left);
      cards.right.removeEventListener('click', handlers.right);
    },
  };
}
